import React, { FC, useState, Fragment } from 'react'
import {
  Grid,
  Divider,
} from '@mui/material'
import {
  useDeleteManufacturerAnnouncementCategoryRequest,
  useEditManufacturerAnnouncementCategoryRequest,
} from 'src/hooks/api'
import { ManufacturerAnnouncementCategory } from 'src/api'
import { useAddPopupMessage, useDialog } from 'src/hooks/ui'
import { DialogNames } from 'src/enums'
import { InlineType } from './inlineType'
import { EditableType } from './editableType'
import { RemoveTypeDialog } from './removeTypeDialog'

interface Props {
  data: ManufacturerAnnouncementCategory[];
  onChange: () => void;
}

export const CategoryList: FC<Props> = ({ data, onChange }) => {
  const [editId, setEditId] = useState<number | null>(null)
  const [removeItem, setRemoveItem] = useState<{
    id: number;
    name: string;
  } | null>(null)
  const addMessage = useAddPopupMessage()
  const { openDialog, closeDialog } = useDialog(DialogNames.DeleteManufacturerAnnouncementCategory)

  const removeCategoryRequest = useDeleteManufacturerAnnouncementCategoryRequest({
    options: {
      onSuccess: onChange,
      onError: (error) => addMessage({ type: 'error', text: error.message }),
    }
  })
  const updateCategoryRequest = useEditManufacturerAnnouncementCategoryRequest({
    id: editId as number,
    options: {
      onSuccess: () => {
        updateEditId(null)
        onChange()
      },
      onError: (error) => addMessage({ type: 'error', text: error.message }),
    }
  })

  const openRemoveDialog = (type: { id: number; name: string }) => {
    setRemoveItem(type)
    openDialog()
  }

  const closeRemoveDialog = () => {
    closeDialog(() => setRemoveItem(null))
  }

  const onRemove = () => {
    if (removeItem !== null) {
      removeCategoryRequest.mutate({ id: removeItem.id })
      closeRemoveDialog()
    }
  }

  const onEdit = ({ displayName }: { displayName: string }) => {
    updateCategoryRequest.mutate({ displayName })
  }

  const updateEditId = (id: number | null) => {
    setEditId(id)
  }

  return (
    <>
      <Grid container direction="column" spacing={2}>
        {data.map(({ id, displayName }, idx, list) => (
          <Fragment key={idx}>
            <Grid item container spacing={1} alignItems="center">
              {editId === id ? (
                <EditableType
                  placeholder='New Category Name'
                  name={displayName || ''}
                  isLoading={updateCategoryRequest.isLoading}
                  onChange={onEdit}
                  onCancel={() => updateEditId(null)}
                />
              ) : (
                <InlineType
                  id={id as number}
                  name={displayName || ''}
                  onEdit={updateEditId}
                  onDelete={openRemoveDialog}
                />
              )}
            </Grid>
            {idx !== list.length - 1 && (
              <Divider flexItem={true} sx={{ ml: 2, mt: 2 }} />
            )}
          </Fragment>
        ))}
      </Grid>
      <RemoveTypeDialog
        dialogName={DialogNames.DeleteManufacturerAnnouncementCategory}
        name={removeItem?.name || ''}
        onDelete={onRemove}
        onCancel={closeRemoveDialog}
      />
    </>
  )
}